"use client";

import { useState, useRef } from "react";
import { X, Upload, Trash2, RotateCcw, Download } from "lucide-react";
import { Button } from "antd";

export default function PractitionerUpload({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);

  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFiles) => {
    if (!selectedFiles) return;

    const validFiles = [];
    const rejectedFiles = [];

    Array.from(selectedFiles).forEach((file, index) => {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      // Validate file type
      if (!fileExtension || !["csv", "xlsx", "xls"].includes(fileExtension)) {
        rejectedFiles.push(
          `${file.name} - Invalid file type. Only CSV and Excel files are allowed.`
        );
        return;
      }

      // Validate file size (5MB limit)
      if (fileSizeMB > 5) {
        rejectedFiles.push(
          `${file.name} - File size exceeds 5MB limit (${fileSizeMB.toFixed(
            1
          )}MB)`
        );
        return;
      }

      validFiles.push({
        id: Date.now().toString() + index,
        name: file.name,
        size: `${fileSizeMB.toFixed(1)} MB`,
        progress: 0,
        status: "uploading",
      });
    });

    // Show rejected files alert if any
    if (rejectedFiles.length > 0) {
      alert(`Some files were rejected:\n\n${rejectedFiles.join("\n")}`);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);

      // Simulate upload progress for valid files
      validFiles.forEach((file) => {
        simulateUpload(file.id);
      });
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
      return (
        <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center">
          <span className="text-white text-xs font-bold">CSV</span>
        </div>
      );
    } else if (extension === "xlsx" || extension === "xls") {
      return (
        <div className="w-8 h-8 bg-green-700 rounded flex items-center justify-center">
          <span className="text-white text-xs font-bold">XLS</span>
        </div>
      );
    }

    return (
      <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center">
        <span className="text-white text-xs font-medium">?</span>
      </div>
    );
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 10 + 5; // Random progress between 5-15%

      setFiles((prev) =>
        prev.map((file) => {
          if (file.id === fileId && file.status === "uploading") {
            const newProgress = Math.min(progress, 100);
            const shouldFail = Math.random() < 0.2 && newProgress > 50; // 20% chance to fail after 50%

            if (shouldFail) {
              clearInterval(interval);
              return { ...file, progress: newProgress, status: "failed" };
            }

            if (newProgress >= 100) {
              clearInterval(interval);
              return { ...file, progress: 100, status: "completed" };
            }

            return { ...file, progress: newProgress };
          }
          return file;
        })
      );

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 300 + Math.random() * 200); // Random interval between 300-500ms
  };

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const removeFile = (id) => {
    setFiles(files.filter((file) => file.id !== id));
  };

  const retryFile = (id) => {
    setFiles(
      files.map((file) =>
        file.id === id ? { ...file, status: "uploading", progress: 0 } : file
      )
    );
    // Restart the upload simulation for the retried file
    setTimeout(() => simulateUpload(id), 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 font-poppins">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[calc(90*var(--vh))] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#E2E8F0]">
          <div>
            <h2 className="text-2xl font-bold text-[#171717] font-poppins mb-1">
              Bulk Upload Practitioners
            </h2>
            <p className="mt-0 mb-0 text-sm text-[#64748B]">
              Easily upload multiple members using a CSV file.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-transparent border-0 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
              isDragOver
                ? "border-[#2563EB] bg-[#EFF6FF]"
                : "border-[#E2E8F0] bg-[#F8FAFC]"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center space-y-4">
              <div className="w-14 h-14 bg-[#EFF6FF] rounded-xl flex items-center justify-center">
                <Upload className="w-7 h-7 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-base font-semibold text-[#171717] mb-0">
                  <span className="text-[#2563EB]">
                    Drag & Drop
                  </span>{" "}
                  your files here
                </p>
                <p className="text-[#64748B] text-[13px] mt-1 mb-0">
                  Accepted formats: .csv, .xlsx | Max Size: 5 MB
                </p>
              </div>
              <Button
                variant="secondary"
                className="h-9 px-[14px] bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-[#171717] text-[13px] font-semibold font-poppins hover:bg-[#F8FAFC]"
                onClick={handleChooseFiles}
              >
                Choose Files
              </Button>
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              {files.map((file) => (
                <div key={file.id} className="bg-white border-[1px] border-solid border-[#E2E8F0] shadow-sm rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {getFileIcon(file.name)}
                      <div>
                        <p className="font-medium text-[#171717]">{file.name}</p>
                        <p className="text-sm text-[#64748B]">{file.size}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {file.status === "uploading" && (
                        <>
                          <span className="text-sm font-semibold text-[#334155]">
                            {file.progress}%
                          </span>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {file.status === "completed" && (
                        <>
                          <span className="text-sm font-medium text-green-600">
                            Completed
                          </span>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {file.status === "failed" && (
                        <>
                          <span className="text-sm font-medium text-red-600">
                            Failed
                          </span>
                          <button
                            onClick={() => retryFile(file.id)}
                            className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                          >
                            <RotateCcw className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full bg-[#F1F5F9] rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        file.status === "completed"
                          ? "bg-green-500"
                          : file.status === "failed"
                          ? "bg-red-500"
                          : "bg-[#2563EB]"
                      }`}
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Download Sample Template */}
          <div className="mt-6 p-4 bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-base text-[#171717] mb-0">
                  Download Sample Template
                </p>
                <p className="text-[#64748B] text-[13px] mt-0.5 mb-0">
                  Make sure the data matches the sample format provided.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-0 hover:bg-[#EFF6FF] bg-transparent text-[13px] text-[#2563EB] font-semibold font-poppins rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" color="#2563EB" />
                Download Sample
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 h-auto py-3 border-[1px] border-solid border-[#E2E8F0] rounded-xl text-base font-semibold text-[#334155] bg-white hover:bg-gray-50 font-poppins"
            >
              Cancel
            </Button>
            <Button className="flex-1 h-auto py-3 btn-gradient border-0 rounded-xl text-base font-semibold font-poppins">
              Upload File
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
